import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import {
  Box,
  Card,
  Grid,
  makeStyles,
  Typography,
  Divider,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { useIntl } from 'react-intl';
import SectionContainer from './SectionContainer';
import TranslatedText from './TranslatedText';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 1200,
  },
  card: {
    margin: `0 ${theme.spacing(2)}px ${theme.spacing(4)}px`,
    boxSizing: 'border-box',
    borderRadius: 5,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,0.2)',
  },
  cardQuestionContainer: {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
    minHeight: 78,
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  expandIconContainer: {
    width: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: '19px',
  },
  cardAnswer: {
    color: theme.palette.text.hint,
    '& > a': {
      color: theme.palette.text.hint,
      textDecoration: 'none',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  },
}));

const AnswerElementWithLink = ({ label, linkLabel, link }) => (
  <>
    {`${label} `}
    <Link to={`/${link}`}>{linkLabel}</Link>
    .
  </>
);

const QAndA = ({ list }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [openedItem, setOpenedItem] = useState(null);
  const handleExpandItem = useCallback((i) => (
    openedItem === i ? setOpenedItem(null) : setOpenedItem(i)), [openedItem]);
  const dividingLimit = Math.ceil(list.length / 2);
  const leftItems = list.slice(0, dividingLimit);
  const rightItems = list.slice(dividingLimit);

  const renderCard = ({ question, answer, replaceWithLink }, idx) => {
    const answerTranslated = intl.formatMessage({ id: answer });
    return (
      <Card key={`qa-${idx}`} classes={{ root: classes.card }}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            className={classes.cardQuestionContainer}
            onClick={() => handleExpandItem(idx)}
          >
            <Box flex={1}>
              <Typography variant="body1">
                <TranslatedText id={question} />
              </Typography>
            </Box>
            <Box className={classes.expandIconContainer}>
              {openedItem === idx ? <RemoveIcon /> : <AddIcon />}
            </Box>
          </Box>
          {openedItem === idx && (
            <>
              <Divider variant="middle" />
              <Box px={3} py={2} display="flex" alignItems="center">
                <Typography className={classes.cardAnswer} variant="body2">
                  {replaceWithLink ? (
                    <AnswerElementWithLink
                      label={answerTranslated.replace(replaceWithLink[intl.locale], '')}
                      linkLabel={replaceWithLink[intl.locale]}
                      link={replaceWithLink.link[intl.locale]}
                    />
                  ) : (
                    <TranslatedText
                      values={{
                        italic: (text) => `<span style='font-style:italic;'>${text}</span>`,
                      }}
                      id={answer}
                    />
                  )}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Card>
    );
  };

  return (
    <SectionContainer
      header={<TranslatedText id="qAndA.header" />}
    >
      <Box className={classes.container}>
        <Grid container>
          <Grid item xs={12} md={6}>
            {leftItems.map((item, i) => renderCard(item, i))}
          </Grid>
          <Grid item xs={12} md={6}>
            {rightItems.map((item, i) => renderCard(item, i + dividingLimit))}
          </Grid>
        </Grid>
      </Box>
    </SectionContainer>
  );
};

QAndA.propTypes = {
  list: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.string,
    answer: PropTypes.string,
  })).isRequired,
};

export default QAndA;
